import { TreeNode } from "./types";

export const extractLeafKeys = (compositeKey: string) => compositeKey.split("_");
export const getLeafKey = (compositeKeys: string[]) => compositeKeys.join("_");

export const upsertTreeNode = (
	list: TreeNode[],
	id: string,
	createNode: () => TreeNode,
	sortFn?: (a: TreeNode, b: TreeNode) => number
): { node: TreeNode; created: boolean } => {
	let node = list.find((n) => n._id === id);
	if (!node) {
		node = createNode();
		list.push(node);
		if (sortFn) list.sort(sortFn);
		return { node, created: true };
	}
	return { node, created: false };
};

export const removeTreeNodes = (nodes: TreeNode[], idsToRemove: string[]): { nodes: TreeNode[]; changed: boolean } => {
	let changed = false;
	const result: TreeNode[] = [];

	for (const node of nodes) {
		if (idsToRemove.includes(node._id)) {
			changed = true;
			continue;
		}

		if (node.list?.length) {
			const childResult = removeTreeNodes(node.list, idsToRemove);
			if (childResult.changed) {
				changed = true;
				if (childResult.nodes.length > 0) {
					result.push({ ...node, list: childResult.nodes });
				} else {
					if (!node.bold) {
						continue;
					}
					result.push({ ...node, list: [] });
				}
				continue;
			}
		}

		result.push(node);
	}

	return { nodes: result, changed };
};

/**
 * Performs a depth-first search on tree nodes to find and update a matching node.
 */
export const updateTreeNode = (
	nodes: TreeNode[] | undefined,
	matcher: (node: TreeNode) => boolean,
	updater: (node: TreeNode) => TreeNode
): { nodes?: TreeNode[]; changed: boolean } => {
	if (!nodes) return { nodes, changed: false };

	let found = false;
	const result: TreeNode[] = [];

	for (let i = 0; i < nodes.length; i++) {
		const node = nodes[i];
		let nextNode = node;

		if (matcher(node)) {
			nextNode = updater(node);
			result.push(nextNode);
			found = true;
			// Append remaining siblings unchanged and stop searching
			for (let j = i + 1; j < nodes.length; j++) {
				result.push(nodes[j]);
			}
			break;
		}

		const childResult = updateTreeNode(node.list, matcher, updater);
		if (childResult.changed) {
			nextNode = { ...nextNode, list: childResult.nodes };
			result.push(nextNode);
			found = true;
			// Append remaining siblings unchanged and stop searching
			for (let j = i + 1; j < nodes.length; j++) {
				result.push(nodes[j]);
			}
			break;
		}

		result.push(nextNode);
	}

	return { nodes: found ? result : nodes, changed: found };
};
